import { FilterOptions } from "entities/customerTickets/types";
import { xorBy } from "lodash";
import { MouseEvent, useMemo, useState } from "react";
import { FilterOption, FilterProps } from "shared/components/Filter";
import { useAnchorEl } from "shared/hooks/useAnchorEl";
import { FilterHook } from "../types";
import { filterConfigs } from "./utils";

export const useTicketsFilter = (): FilterHook => {
  const [selectedValues, setSelectedValues] = useState<{
    [key: string]: FilterOption[];
  }>({});
  const [openStates, setOpenStates] = useState<{ [key: string]: boolean }>({});
  const { anchorEl, handleAnchorClose, handleAnchorOpen } = useAnchorEl();

  const filtersData: FilterProps[] = filterConfigs.map(
    ({ key, options, label }) => {
      const handleChangeValue = (
        _: MouseEvent<HTMLElement>,
        option: FilterOption,
      ) => {
        setSelectedValues((prev) => {
          const currentOptions = prev[key] || [];

          const isAllSelected = currentOptions.some(
            (o) => o.value === FilterOptions.all,
          );
          const isOptionSelected = currentOptions.some(
            (o) => o.value === option.value,
          );
          if (option.value === FilterOptions.all) {
            return {
              ...prev,
              [key]: isAllSelected ? [] : [option],
            };
          }
          const newValues = isOptionSelected
            ? currentOptions.filter((o) => o.value !== option.value)
            : xorBy(currentOptions, [option], "value").filter(
                (o) => o.value !== FilterOptions.all,
              );
          return {
            ...prev,
            [key]: newValues,
          };
        });
      };

      const onClearSelectedValues = () =>
        setSelectedValues((prev) => ({ ...prev, [key]: [] }));

      const handleOpenMenu = (event: MouseEvent<HTMLButtonElement>) => {
        setOpenStates((prev) => ({ ...prev, [key]: true }));
        handleAnchorOpen(event);
      };

      const handleClose = () => {
        setOpenStates((prev) => ({ ...prev, [key]: false }));
        handleAnchorClose();
      };

      return {
        title: label,
        options,
        anchorEl,
        open: openStates[key] || false,
        selectedValue: selectedValues[key] || [],
        handleOpenMenu,
        handleChangeValue,
        handleClose,
        onClearSelectedValues,
        disabled: false,
      };
    },
  );

  const filteredValues = useMemo(() => {
    return Object.entries(selectedValues).reduce(
      (acc, [key, options]) => {
        acc[key] = options.map((option) => option.value);
        return acc;
      },
      {} as { [key: string]: string[] },
    );
  }, [selectedValues]);

  return {
    filtersData,
    filteredValues,
    setFilteredValues: setSelectedValues,
  };
};
